import { useRef, useState, forwardRef, useImperativeHandle } from 'react'

import { maskCardNumber, formatExpiryDate } from 'credit-cards/utils'

import Button from '@components/Button'
import CircleLoader from '@components/CircleLoader'
import CreditCardFormComponent from 'credit-cards/pagseguro/components/CreditCardFormComponent'

import 'credit-cards/moneris/components/styles.scss'

const AddCreditCardForm = forwardRef(
    ({ isTokenizationPage, errorMessage, addCreditCardHandler, resetErrorState, customRef }) => {
        const internalFormRef = useRef(null)
        const [isLoading, setIsLoading] = useState(false)
        const [cardHolder, setCardHolder] = useState('')
        const [cardLastNumbers, setCardLastNumbers] = useState('')
        const [expDate, setExpDate] = useState('')
        const [token, setToken] = useState('')
        const [error, setError] = useState(null)
        const [isInvisible, setInvisible] = useState(false)
        const showLoader = isTokenizationPage

        useImperativeHandle(customRef, () => ({
            reset: () => resetForm(),
        }))

        const resetForm = () => {
            setToken('')
            setCardHolder('')
            setCardLastNumbers('')
            setExpDate('')
            setIsLoading(false)
            setError('')
            internalFormRef.current.reset()
        }

        const setErrorHandler = error => {
            setError(error)
            setIsLoading(false)
        }

        const handleSubmitDefaultBehaviour = event => event.preventDefault()

        const handleKeyDown = event => {
            if (event.key === ' ') {
                event.preventDefault()
            }
        }

        const tokenizeCard = (model, reset) => {
            !isTokenizationPage && resetErrorState()
            setError(null)

            const { cardholdersName, cardNumber, expiryMonth, expiryYear, ccv } = model

            const card = window.PagSeguro.encryptCard({
                publicKey: window.PAGSEGURO_KEY,
                holder: cardholdersName,
                number: cardNumber,
                expMonth: expiryMonth,
                expYear: expiryYear,
                securityCode: ccv,
            })

            const { encryptedCard, hasErrors, errors } = card

            if (errors?.length > 0) {
                setErrorHandler(<T>Moneris.Creditcard.Error.General</T>)
                setIsLoading(false)
            }
            if (encryptedCard && !hasErrors && errors.length === 0) {
                setIsLoading(true)
                showLoader && setInvisible(true)

                setToken(encryptedCard)
                setCardHolder(cardholdersName)
                setCardLastNumbers(maskCardNumber(cardNumber))
                setExpDate(formatExpiryDate(expiryMonth, expiryYear))

                addCreditCardHandler &&
                    addCreditCardHandler(
                        cardholdersName,
                        encryptedCard,
                        formatExpiryDate(expiryMonth, expiryYear),
                        maskCardNumber(cardNumber)
                    )

                isTokenizationPage && reset()
            }
        }

        React.useEffect(() => {
            if (errorMessage) {
                setErrorHandler(errorMessage)
            }
        }, [errorMessage])

        return (
            <>
                <div className="moneris d-flex flex-column align-items-left">
                    {isLoading && showLoader && <CircleLoader className="loader" />}
                    <>
                        <p data-pagseguro-token={token} className="invisible" />
                        <p data-pagseguro-cardholder={cardHolder} className="invisible" />
                        <p data-pagseguro-exp-date={expDate} className="invisible" />
                        <p data-pagseguro-card-last4={cardLastNumbers} className="invisible" />
                    </>
                    <CreditCardFormComponent
                        submit={tokenizeCard}
                        isInvisible={isInvisible}
                        formRef={internalFormRef}
                        isLoading={isLoading}
                        handleSubmit={handleSubmitDefaultBehaviour}
                        error={error || errorMessage}
                        handleKeyDown={handleKeyDown}
                        zip
                    />
                </div>
            </>
        )
    }
)

export default withTranslate(AddCreditCardForm)
